import { FieldTypes } from '../../constants'
import { DropdownOptionItem, Field, FieldMap, MultipleSelectField } from '../../types/forms/fields'
import { getConditionOrDiseaseFields } from './shared'

const anemiaOrBloodDisorderOptions: Array<DropdownOptionItem> = [
  {
    text: 'Anemia',
    id: 'anemia',
  },
  {
    text: 'Thalassemia',
    id: 'thalassemia',
  },
  {
    text: 'von Willebrand disease',
    id: 'von-willebrand-disease',
  },
  {
    text: 'Hemophilia other than von Willebrand',
    id: 'hemophilia-other',
  },
  {
    text: 'Sickle cell disease or trait',
    id: 'sickle-cell',
  },
  {
    text: 'Other',
    id: 'other',
  },
]

const anemiaOrBloodDisorderFields: Record<string, Field> = getConditionOrDiseaseFields(
  'anemiaOrBloodDisorder',
  'Anemia or blood disorder',
  anemiaOrBloodDisorderOptions,
)

const bloodTransfusionFields: Record<string, Field> = getConditionOrDiseaseFields(
  'bloodTransfusion',
  'Blood transfusion',
)

const autoimmuneDiseaseOptions: Array<DropdownOptionItem> = [
  {
    text: 'Rheumatoid arthritis',
    id: 'rheumatoid-arthritis',
  },
  {
    text: 'Lupus',
    id: 'lupus',
  },
  {
    text: 'Multiple sclerosis',
    id: 'multiple-sclerosis',
  },
  {
    text: 'Psoriasis',
    id: 'psoriasis',
  },
  {
    text: 'Eczema',
    id: 'eczema',
  },
  {
    text: 'Other',
    id: 'other',
  },
]

const autoimmuneDiseaseFields: Record<string, Field> = getConditionOrDiseaseFields(
  'autoimmuneDisease',
  'Autoimmune disease',
  autoimmuneDiseaseOptions,
)

const cancerFields: Record<string, Field> = getConditionOrDiseaseFields('cancer', 'Cancer')

const chronicFatigueOptions: Array<DropdownOptionItem> = [
  {
    text: 'Chronic fatigue syndrome',
    id: 'chronic-fatigue',
  },
  {
    text: 'Lyme disease',
    id: 'lyme-disease',
  },
  {
    text: 'Fibromyalgia',
    id: 'fibromyalgia',
  },
  {
    text: 'Other',
    id: 'other',
  },
]
const chronicFatigueFields: Record<string, Field> = getConditionOrDiseaseFields(
  'chronicFatigue',
  'Chronic fatigue, neuromuscular, or bacterial syndrome',
  chronicFatigueOptions,
)

const chronicOrSeriousENTOptions: Array<DropdownOptionItem> = [
  {
    text: 'Chronic sinusitis',
    id: 'chronic-sinusitis',
  },
  {
    text: 'Tonsillitis',
    id: 'tonsillitis',
  },
  {
    text: 'Strep',
    id: 'strep',
  },
  {
    text: 'Snoring or sleep apnea',
    id: 'snoring-or-sleep-apnea',
  },
  {
    text: 'Other',
    id: 'other',
  },
]
const chronicOrSeriousENTFields: Record<string, Field> = getConditionOrDiseaseFields(
  'chronicOrSeriousEnt',
  'Chronic or serious ENT disease or condition',
  chronicOrSeriousENTOptions,
)

const respiratoryOrLungIllnessOptions: Array<DropdownOptionItem> = [
  {
    text: 'Asthma',
    id: 'asthma',
  },
  {
    text: 'Chronic cough',
    id: 'chronic-cough',
  },
  {
    text: 'Shortness of breath',
    id: 'shortness-of-breath',
  },
  {
    text: 'Tuberculosis, current or past',
    id: 'tuberculosis',
  },
  {
    text: 'Other',
    id: 'other',
  },
]

const respiratoryOrLungIllnessFields: Record<string, Field> = getConditionOrDiseaseFields(
  'respiratoryOrLungIllness',
  'Respiratory or lung illness',
  respiratoryOrLungIllnessOptions,
)

const hypertensionOptions: Array<DropdownOptionItem> = [
  {
    text: 'During pregnancy',
    id: 'during-pregnancy',
  },
  {
    text: 'Chronic or essential hypertension',
    id: 'chronic-or-essential-hypertension',
  },
]
const hypertensionFields: Record<string, Field> = getConditionOrDiseaseFields(
  'hypertension',
  'High blood pressure (hypertension)',
  hypertensionOptions,
)

const heartDiseaseOrDefectOptions: Array<DropdownOptionItem> = [
  {
    text: 'Heart disease',
    id: 'heart-disease',
  },
  {
    text: 'Heart attack',
    id: 'heart-attack',
  },
  {
    text: 'Congenital heart defect',
    id: 'congenital-heart-defect',
  },
]

const heartDiseaseOrDefectFields: Record<string, Field> = getConditionOrDiseaseFields(
  'heartDiseaseOrDefect',
  'Heart disease or defect',
  heartDiseaseOrDefectOptions,
  'Details (include surgeries, symptoms, treatments, therapies, etc.)',
)

const varicoseVeinsBloodClotsStrokeThrombosisOptions: Array<DropdownOptionItem> = [
  {
    text: 'Varicose veins',
    id: 'varicose-veins',
  },
  {
    text: 'Blood clots',
    id: 'blood-clots',
  },
  {
    text: 'Stroke',
    id: 'stroke',
  },
  {
    text: 'Deep vein thrombosis (DVT)',
    id: 'deep-vein-thrombosis',
  },
  {
    text: 'Pulmonary embolism',
    id: 'pulmonary-embolism',
  },
  {
    text: 'Other',
    id: 'other',
  },
]
const varicoseVeinsBloodClotsStrokeThrombosisFields: Record<string, Field> =
  getConditionOrDiseaseFields(
    'varicoseVeinsBloodClotsStrokeThrombosis',
    'Varicose veins, blood clots, stroke or thrombosis',
    varicoseVeinsBloodClotsStrokeThrombosisOptions,
  )

const hepatitisOrOtherLiverDiseaseOptions: Array<DropdownOptionItem> = [
  {
    text: 'Hepatitis A',
    id: 'hepatitis-a',
  },
  {
    text: 'Hepatitis B',
    id: 'hepatitis-b',
  },
  {
    text: 'Hepatitis C',
    id: 'hepatitis-c',
  },
  {
    text: 'Other',
    id: 'other',
  },
]

const hepatitisOrOtherLiverDiseaseFields: Record<string, Field> = getConditionOrDiseaseFields(
  'hepatitisOrOtherLiverDisease',
  'Hepatitis or other liver disease',
  hepatitisOrOtherLiverDiseaseOptions,
)

const hivAidsFields: Record<string, Field> = getConditionOrDiseaseFields('hivAids', 'HIV/AIDS')

const diabetesOptions: Array<DropdownOptionItem> = [
  {
    text: 'Type 1 diabetes',
    id: 'type-1-diabetes',
  },
  {
    text: 'Type 2 diabetes',
    id: 'type-2-diabetes',
  },
  {
    text: 'Gestational diabetes (pregnancy-related)',
    id: 'gestational-diabetes',
  },
]

const diabetesManagementStrategyField: MultipleSelectField = {
  type: FieldTypes.MULTIPLE_SELECT,
  placeholder: 'How do you manage your diabetes?',
  condition: v => !!v?.diabetes,
  options: [
    {
      text: 'Diet and lifestlye-controlled',
      id: 'diet-and-lifestyle-controlled',
    },
    { text: 'Oral medications', id: 'oral-medications' },
    {
      text: 'Insulin injections',
      id: 'insulin-injections',
    },
    {
      text: 'Insulin pump',
      id: 'insulin-pump',
    },
  ],
}

const diabetesFields: Record<string, Field> = getConditionOrDiseaseFields(
  'diabetes',
  'Diabetes',
  diabetesOptions,
  undefined,
  {
    diabetesManagementStrategy: diabetesManagementStrategyField,
  },
)

const thyroidDisorderOptions: Array<DropdownOptionItem> = [
  {
    text: "Grave's disease",
    id: 'graves-disease',
  },
  {
    text: "Hashimoto's thyroiditis",
    id: 'hashimotos-thyroiditis',
  },
  {
    text: 'Thyroid nodule(s)',
    id: 'thyroid-nodules',
  },
  {
    text: 'Other',
    id: 'other',
  },
]

const thyroidDisorderFields: Record<string, Field> = getConditionOrDiseaseFields(
  'thyroidDisorder',
  'Thyroid disorder',
  thyroidDisorderOptions,
)

const gastrointestinalConditionOrDiseaseOptions: Array<DropdownOptionItem> = [
  {
    text: 'Ulcerative colitis',
    id: 'ulcerative-colitis',
  },
  {
    text: "Crohn's disease",
    id: 'crohns-disease',
  },
  {
    text: 'Irritable bowel syndrome (IBS)',
    id: 'irritable-bowel-syndrome',
  },
  {
    text: 'Unexplained abdominal pain',
    id: 'unexplained-abdominal-pain',
  },
  {
    text: 'Hemorrhoids',
    id: 'hemorrhoids',
  },
  {
    text: 'Colon polyps',
    id: 'colon-polyps',
  },
  {
    text: 'Recurrent constipation',
    id: 'recurrent-constipation',
  },
  {
    text: 'Recurrent diarrhea',
    id: 'recurrent-diarrhea',
  },
  {
    text: 'Other',
    id: 'other',
  },
]

const gastrointestinalConditionOrDiseaseFields: Record<string, Field> = getConditionOrDiseaseFields(
  'gastrointestinalConditionOrDisease',
  'Gastrointestinal condition or disease',
  gastrointestinalConditionOrDiseaseOptions,
)

const kidneyOrBladderDiseaseOrDisorderOptions: Array<DropdownOptionItem> = [
  {
    text: 'UTI',
    id: 'uti',
  },
  {
    text: 'Incontinence or leaking when you cough, sneeze, or laugh',
    id: 'incontinence-or-leaking',
  },
  {
    text: 'Other',
    id: 'other',
  },
]

const kidneyOrBladderDiseaseOrDisorderFields: Record<string, Field> = getConditionOrDiseaseFields(
  'kidneyOrBladderDiseaseOrDisorder',
  'Kidney or bladder disease or disorder, including UTI or incontinence',
  kidneyOrBladderDiseaseOrDisorderOptions,
)

const backOrSacralInjuryFields: Record<string, Field> = getConditionOrDiseaseFields(
  'backOrSacralInjury',
  'Back or sacral injury',
)

const blindnessBeforeAge40Fields: Record<string, Field> = getConditionOrDiseaseFields(
  'blindnessBeforeAge40',
  'Blindness from birth or before age 40',
)

const deafnessBeforeAge40Fields: Record<string, Field> = getConditionOrDiseaseFields(
  'deafnessBeforeAge40',
  'Deafness from birth or before age 40',
)

const birthDefectOrOtherGeneticDisorderFields: Record<string, Field> = getConditionOrDiseaseFields(
  'birthDefectOrOtherGeneticDisorder',
  'Birth defect, congenital malformation, or other genetic disorder not listed elsewhere',
)

const tongueTieFields: Record<string, Field> = getConditionOrDiseaseFields(
  'tongueTie',
  'Tongue tie',
)

const exposureToChildhoodIllnessesOptions: Array<DropdownOptionItem> = [
  {
    text: 'Chicken pox or shingles',
    id: 'chicken-pox-or-shingles',
  },
  {
    text: 'Measles',
    id: 'measles',
  },
  {
    text: 'Mumps',
    id: 'mumps',
  },
  {
    text: 'Rubella',
    id: 'rubella',
  },
  {
    text: 'Pertussis',
    id: 'pertussis',
  },
  {
    text: 'Other',
    id: 'other',
  },
]

const exposureToChildhoodIllnessesFields: Record<string, Field> = getConditionOrDiseaseFields(
  'exposureToChildhoodIllnesses',
  'Exposure to childhood illnesses',
  exposureToChildhoodIllnessesOptions,
)

const exposureToChemicalsFields: Record<string, Field> = getConditionOrDiseaseFields(
  'exposureToChemicals',
  'Exposure to toxic or harmful chemicals at home or work on a regular basis',
)

export const conditionsOrDiseasesFieldGroups: Array<Record<string, Field>> = [
  anemiaOrBloodDisorderFields,
  bloodTransfusionFields,
  autoimmuneDiseaseFields,
  cancerFields,
  chronicFatigueFields,
  chronicOrSeriousENTFields,
  respiratoryOrLungIllnessFields,
  hypertensionFields,
  heartDiseaseOrDefectFields,
  varicoseVeinsBloodClotsStrokeThrombosisFields,
  hepatitisOrOtherLiverDiseaseFields,
  hivAidsFields,
  diabetesFields,
  thyroidDisorderFields,
  gastrointestinalConditionOrDiseaseFields,
  kidneyOrBladderDiseaseOrDisorderFields,
  backOrSacralInjuryFields,
  blindnessBeforeAge40Fields,
  deafnessBeforeAge40Fields,
  birthDefectOrOtherGeneticDisorderFields,
  tongueTieFields,
  exposureToChildhoodIllnessesFields,
  exposureToChemicalsFields,
]

export const conditionsOrDiseasesField: FieldMap = {
  name: 'Conditions',
  children: {
    ...anemiaOrBloodDisorderFields,
    ...bloodTransfusionFields,
    ...autoimmuneDiseaseFields,
    ...cancerFields,
    ...chronicFatigueFields,
    ...chronicOrSeriousENTFields,
    ...respiratoryOrLungIllnessFields,
    ...hypertensionFields,
    ...heartDiseaseOrDefectFields,
    ...varicoseVeinsBloodClotsStrokeThrombosisFields,
    ...hepatitisOrOtherLiverDiseaseFields,
    ...hivAidsFields,
    ...diabetesFields,
    ...thyroidDisorderFields,
    ...gastrointestinalConditionOrDiseaseFields,
    ...kidneyOrBladderDiseaseOrDisorderFields,
    ...backOrSacralInjuryFields,
    ...blindnessBeforeAge40Fields,
    ...deafnessBeforeAge40Fields,
    ...birthDefectOrOtherGeneticDisorderFields,
    ...tongueTieFields,
    ...exposureToChildhoodIllnessesFields,
    ...exposureToChemicalsFields,
  },
}
